import { useRef, useState, type ReactNode } from 'react'
import { Check, ChevronDown } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

export interface InlineSelectOption<T extends string> {
  value: T
  label: ReactNode
}

/**
 * A table cell that reads as plain text until the user means to change it,
 * then opens a popover of options — the list's "edit in place" pattern for
 * Kept at and Zone (design note §1).
 *
 * Deliberately controlled, not optimistic: `value` always comes from the
 * caller's data, never from local state here. A row that keeps its value on a
 * refused change (Review Focus 2) falls out of that for free — nothing here
 * ever shows anything the server has not actually confirmed.
 */
export function InlineSelect<T extends string>({
  value,
  options,
  onChange,
  readOnly,
  renderValue,
  header,
  footer,
  ariaLabel,
}: {
  value: T
  options: InlineSelectOption<T>[]
  onChange: (next: T) => void
  /** No permission to edit: render `renderValue` as plain text, no popover. */
  readOnly?: boolean
  renderValue: (value: T) => ReactNode
  header?: ReactNode
  footer?: ReactNode
  ariaLabel: string
}) {
  const [open, setOpen] = useState(false)
  const listRef = useRef<HTMLDivElement>(null)

  if (readOnly) {
    return <span className='inline-flex items-center text-sm'>{renderValue(value)}</span>
  }

  function optionEls() {
    return listRef.current?.querySelectorAll<HTMLButtonElement>('[data-inline-option]') ?? null
  }

  function focusOption(index: number) {
    const items = optionEls()
    if (!items || items.length === 0) return
    const clamped = (index + items.length) % items.length
    items[clamped]?.focus()
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type='button'
          aria-label={ariaLabel}
          aria-haspopup='listbox'
          aria-expanded={open}
          className={cn(
            'group inline-flex h-7 items-center gap-1.5 rounded-md border border-transparent px-1.5 text-sm outline-none',
            'hover:border-border hover:bg-background hover:shadow-sm focus-visible:border-border focus-visible:bg-background focus-visible:shadow-sm',
            open && 'border-border bg-background shadow-sm',
          )}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              setOpen(true)
              requestAnimationFrame(() => focusOption(0))
            }
          }}
        >
          {renderValue(value)}
          <ChevronDown
            className={cn(
              'h-3 w-3 shrink-0 text-muted-foreground opacity-0 transition-opacity',
              'group-hover:opacity-100 group-focus-visible:opacity-100',
              open && 'opacity-100',
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align='start'
        className='w-80 p-1'
        onKeyDown={(e) => {
          if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
          e.preventDefault()
          const items = optionEls()
          const current = items ? Array.from(items).indexOf(document.activeElement as HTMLButtonElement) : -1
          focusOption(e.key === 'ArrowDown' ? current + 1 : current - 1)
        }}
      >
        {header && (
          <div className='px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'>
            {header}
          </div>
        )}
        {/* Plain, continuous rows — no border or rounding per option, which is
            what made a column of coloured option badges read as a stack of
            separate pills. The row container (height, padding, hover/selected
            background, focus ring) is owned here; the caller's `label` is the
            row's full content. */}
        <div ref={listRef} role='listbox' aria-label={ariaLabel} className='max-h-72 overflow-y-auto py-1'>
          {options.map((option) => (
            <button
              key={option.value}
              type='button'
              data-inline-option
              role='option'
              aria-selected={option.value === value}
              className={cn(
                'flex h-9 w-full items-center justify-between gap-2 px-3 text-left text-sm outline-none hover:bg-muted',
                'focus-visible:bg-muted focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                option.value === value && 'bg-accent font-medium',
              )}
              onClick={() => {
                setOpen(false)
                // Re-picking what is already there changes nothing: no
                // request, and no Undo toast for a change that never was.
                if (option.value !== value) onChange(option.value)
              }}
            >
              <span className='min-w-0 flex-1'>{option.label}</span>
              {option.value === value && <Check className='h-3.5 w-3.5 shrink-0 text-primary' />}
            </button>
          ))}
        </div>
        {footer && <div className='mt-1 border-t pt-1'>{footer}</div>}
      </PopoverContent>
    </Popover>
  )
}
